import { oc } from "@orpc/contract";
import { z } from "zod";

// ── Response schemas ──

/**
 * A policy tag definition. Every tag definition is an access-control tag that
 * scope rows can reference; there is no descriptive/UI-only tag kind. See
 * `abac_architecture.md` ("Tag definitions").
 */
export const TagDefinitionSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  displayName: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type TagDefinitionDTO = z.infer<typeof TagDefinitionSchema>;

// ── Input schemas ──

export const ListTagDefinitionsInputSchema = z.object({
  // Cap is 200 (not the usual 100): the scope/tag pickers in the privilege
  // editor and the tag-resources card fetch the full policy-tag set in one
  // shot (`limit: 200`, no pagination) to populate a dropdown. The paginated
  // admin list view uses a much smaller page size.
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
  search: z.string().max(255).optional(),
});
export type ListTagDefinitionsInput = z.infer<
  typeof ListTagDefinitionsInputSchema
>;

export const ListTagDefinitionsResponseSchema = z.object({
  tagDefinitions: z.array(TagDefinitionSchema),
  pagination: z.object({
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
  }),
});

export const GetTagDefinitionInputSchema = z.object({
  id: z.string().uuid(),
});

/**
 * `name` is the stable identifier — snake_case alphanumeric/underscore. Per
 * `abac_architecture.md`, name is never renamed casually; use `displayName`
 * for UI labels.
 */
export const CreateTagDefinitionInputSchema = z.object({
  name: z
    .string()
    .min(1)
    .max(255)
    .regex(
      /^[a-z0-9_]+$/,
      "name must be snake_case (lowercase letters, digits, and underscores)",
    ),
  displayName: z.string().min(1).max(255),
});
export type CreateTagDefinitionInput = z.infer<
  typeof CreateTagDefinitionInputSchema
>;

/**
 * `name` is intentionally NOT updatable — the doc explicitly says tag names
 * are never renamed casually. Only the human label is editable.
 */
export const UpdateTagDefinitionInputSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string().min(1).max(255).optional(),
});
export type UpdateTagDefinitionInput = z.infer<
  typeof UpdateTagDefinitionInputSchema
>;

export const DeleteTagDefinitionInputSchema = z.object({
  id: z.string().uuid(),
});
export type DeleteTagDefinitionInput = z.infer<
  typeof DeleteTagDefinitionInputSchema
>;

export const DeleteTagDefinitionResponseSchema = z.object({
  success: z.literal(true),
});

// ── Contract ──
//
// TODO(authz): the architecture doc flags tag mutation as a privileged action
// that may warrant a dedicated RBAC subject (Subject.ResourceTag /
// Subject.TagDefinition). For phase 1 we reuse Subject.ResourcePrivilege so
// privilege admins manage tag-defs too. Revisit before opening tag-mutation
// to non-privilege admins.
export const tagDefinitionsContracts = {
  list: oc
    .input(ListTagDefinitionsInputSchema)
    .output(ListTagDefinitionsResponseSchema)
    .route({ method: "GET" }),
  get: oc
    .input(GetTagDefinitionInputSchema)
    .output(TagDefinitionSchema)
    .route({ method: "GET" }),
  create: oc
    .input(CreateTagDefinitionInputSchema)
    .output(TagDefinitionSchema)
    .route({ method: "POST" }),
  update: oc
    .input(UpdateTagDefinitionInputSchema)
    .output(TagDefinitionSchema)
    .route({ method: "POST" }),
  delete: oc
    .input(DeleteTagDefinitionInputSchema)
    .output(DeleteTagDefinitionResponseSchema)
    .route({ method: "POST" }),
} as const;
